import { useState } from 'react'

interface OfferApplicationData {
  offerId: string
  firstName: string
  lastName: string
  email: string
  message: string | null
  cv: File
  agreed: boolean
}

interface OfferApplicationPayload {
  firstName: string
  lastName: string
  email: string
  message: string | null
  offer: string
  agreed: boolean
}

type SpontaneousApplicationData = {
  firstName: string
  lastName: string
  email: string
  jobType: string
  contractType: string
  startDate: string
  office: string
  message: string
  agreed: boolean
  cv: File
}

export function useOfferApplyRequest({
  backendUrl,
  frontStrapiToken,
}: {
  backendUrl?: string
  frontStrapiToken?: string
}) {
  if (!backendUrl || !frontStrapiToken) {
    throw new Error('backendUrl is required')
  }

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [data, setData] = useState<unknown>(null)
  const [succesMessage, setSuccesMessage] = useState<string | null>(null)

  async function postOfferApplication({
    firstName,
    lastName,
    email,
    message,
    cv,
    agreed,
    offerId,
  }: OfferApplicationData) {
    try {
      setLoading(true)
      const formData = new FormData()
      const payload: OfferApplicationPayload = {
        firstName,
        lastName,
        email,
        message: message || '',
        offer: offerId,
        agreed,
      }
      formData.append('files', cv as File, cv?.name ?? 'cv.pdf')
      formData.append('data', JSON.stringify(payload))

      const response = await fetch(
        `${backendUrl}/api/candidate/application/offer`,
        {
          method: 'post',
          body: formData,
          headers: {
            Authorization: `Bearer ${frontStrapiToken || ''}`,
          },
        },
      )

      if (!response.ok) {
        setError('Une erreur est survenue, veuillez réessayer plus tard.')
        throw new Error('Failed to post application')
      }
      const json = await response.json()
      setData(json)
      setSuccesMessage('Votre candidature a bien été envoyée.')
      setSuccess(true)
    } catch (err) {
      console.error(err)
      setError('Une erreur majeure est survenue, veuillez réessayer plus tard.')
    } finally {
      setLoading(false)
    }
  }

  async function postSpontaneousApplication({
    firstName,
    lastName,
    email,
    message,
    cv,
    agreed,
    contractType,
    jobType,
    startDate,
    office,
  }: SpontaneousApplicationData) {
    try {
      setLoading(true)
      const formData = new FormData()
      const payload: Omit<SpontaneousApplicationData, 'cv'> = {
        firstName,
        lastName,
        email,
        message: message || '',
        office,
        agreed,
        contractType,
        jobType,
        startDate,
      }
      formData.append('files', cv as File, cv?.name ?? 'cv.pdf')
      formData.append('data', JSON.stringify(payload))

      const response = await fetch(
        `${backendUrl}/api/candidate/application/firm`,
        {
          method: 'post',
          headers: {
            Authorization: `Bearer ${frontStrapiToken || ''}`,
          },
          body: formData,
        },
      )

      if (!response.ok) {
        setError('Une erreur est survenue, veuillez réessayer plus tard.')
        throw new Error('Failed to post application')
      }
      const json = await response.json()
      setData(json)
      setSuccesMessage('Votre candidature a bien été envoyée.')
      setSuccess(true)
    } catch (err) {
      console.error(err)
      setError('Une erreur majeure est survenue, veuillez réessayer plus tard.')
    } finally {
      setLoading(false)
    }
  }

  return {
    postOfferApplication,
    postSpontaneousApplication,
    loading,
    error,
    success,
    data,
    succesMessage,
    setSuccess,
  }
}
